import { Component, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { alertService } from '@unpispas/upp-base';
import { languageService, languageSupport } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { TaxIdValidators } from '@unpispas/upp-defs';
import { dataService } from '@unpispas/upp-data';

import { Client } from '@unpispas/upp-data';
import { TicketInvoice } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

import { UppClientChanged } from '@unpispas/feature/client';

@Component({
    selector: 'upp-ticket-invoice',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-invoice.html',
    styleUrls: [
        './upp-invoice.scss'
    ]
})
export class UppTicketInvoiceComponent extends languageSupport implements OnChanges, OnDestroy {
    @Input() ticket: TicketView | null = null;

    @Output() Accept = new EventEmitter<void>();
    @Output() Cancel = new EventEmitter<void>();

    private _invoicing = false;
    private _proceedable = false;
    private _taxIdUnverified = false;
    
    private _ticketSubscription: Subscription | null = null;

    constructor(
        private lang: languageService,
        private change: ChangeDetectorRef,
        private alertCtrl: alertService,
        private data: dataService,
        public view: viewService
    ){
        super(lang, change);
    }

    ngOnChanges(changes: SimpleChanges): void {
        void changes;

        if (this._ticketSubscription){
            this._ticketSubscription.unsubscribe();
            this._ticketSubscription = null;
        }

        this._client = null;

        if (this.ticket){
            this._ticketSubscription = this.ticket.OnViewRefresh.subscribe(
            () => {
                this._client = null;
                this.change.markForCheck();
            });
        }
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._ticketSubscription){
            this._ticketSubscription.unsubscribe();
            this._ticketSubscription = null;
        }
    }

    get place(){
        return this.ticket?.place || null;
    }

    /***********************************/
    /* TICKET INVOICE CLIENT           */
    /***********************************/

    private _client: Client | null = null;

    get Client(): Client {
        if (this._client === null){
            this._client = new Client(null, this.data);
            const _linked = this.ticket?.invoice?.client?.last ?? this.ticket?.invoice?.client;
            if (_linked){
                this._client.name = _linked.name;
                this._client.taxid = _linked.taxid;
                this._client.email = _linked.email;
                this._client.address = _linked.address;
                this._client.postal = _linked.postal;
                this._client.region = _linked.region;
                this._client.country = _linked.country;
            }
        }

        return this._client;
    }

    set Client(value: Client | null){
        this._client = value;
    }

    get CanTicketInvoice(): boolean {
        return this._proceedable;
    }

    OnClientChange(ev: UppClientChanged): void {
        this._proceedable = ev.invoiceProceedable;
        this._taxIdUnverified = ev.taxIdUnverified;
        this.change.markForCheck();
    }

    async OnInvoice(): Promise<void> {
        if (this._invoicing || !this.ticket){
            return;
        }
        this._invoicing = true;

        try {
            if (this.ticket?.ZeroRateInvoice && !TaxIdValidators.IsZeroRate(this.Client?.taxid, this.Client?.country)){
                const _alert = this.alertCtrl.alert({
                    header: this.lang.tr('@place_ticket_invoice_zerorate_title'),
                    message: this.lang.tr('@place_ticket_invoice_zerorate_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                _alert.dismiss(false);
                            }, 100);
                            return false;
                        }
                    }]
                });

                await _alert.present();
                await _alert.onDidDismiss();
                return;
            }

            if (this._taxIdUnverified){
                const _alert = this.alertCtrl.alert({
                    header: this.lang.tr('@place_ticket_invoice_unsafe_taxid_title'),
                    message: this.lang.tr('@place_ticket_invoice_unsafe_taxid_message'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.lang.tr('@yes'),
                        handler: () => {
                            setTimeout(() => {
                                _alert.dismiss(true);
                            }, 100);
                            return false;
                        }
                    }, {
                        text: this.lang.tr('@no'),
                        handler: () => {
                            setTimeout(() => {
                                _alert.dismiss(false);
                            }, 100);
                            return false;
                        }
                    }]
                });

                await _alert.present();
                const _detail = await _alert.onDidDismiss();
                if (_detail.data !== true){
                    return;
                }
            }

            await this._doInvoice();
        }
        finally {
            this._invoicing = false;
        }
    }

    OnCancel(): void {
        if (this.ticket?.pendpay){
            this.ticket.pendpay = null;
        }

        this.Cancel.emit();
    }

    private async _doInvoice(): Promise<void> {
        if (!this.ticket){
            return;
        }

        const _commit = await this._Commit();
        if (!_commit.committed || !_commit.client){
            return;
        }

        const _invoice = new TicketInvoice(null, this.data);
        if (_invoice){
            _invoice.ticket = this.ticket.object;
            _invoice.status = 'AC';
            _invoice.client = _commit.client;
        }

        this.ticket.DoPrint(true);
        this.ticket.invoice = _invoice;

        if (this.ticket.pendpay){
            this.ticket.payment = this.ticket.pendpay;
            this.ticket.pendpay = null;
        }

        this.Accept.emit();
    }

    private _ResolveClient(form: Client): Client | null {
        const _place = this.place?.object;
        if (!this.place || !_place){
            return null;
        }

        const _merchantName = String(form.name || '').trim();
        const _merchantTaxid = String(form.taxid ?? '').trim();
        const _merchantEmail = String(form.email || '').trim();
        const _merchantAddress = String(form.address || '').trim();
        const _merchantPostal = String(form.postal || '').trim();
        const _merchantRegion = String(form.region || '').trim();
        const _merchantCountry = String(form.country || '').trim().toUpperCase() || null;

        let _client: Client | null = null;

        for (const _placeclient of this.place.clients){
            if (form.Same(_placeclient)){
                const _resolved = _placeclient.last;
                if (_resolved.IsValid){
                    _client = _resolved;
                    break;
                }
            }
        }

        if (_client && (_client.address !== _merchantAddress)){
            const _next = form;
            _next.place = _place;
            _next.name = _merchantName;
            _next.taxid = _merchantTaxid;
            _next.email = _merchantEmail;
            _next.address = _merchantAddress;
            _next.postal = _merchantPostal;
            _next.region = _merchantRegion;
            _next.country = _merchantCountry;
            _next.status = 'AC';

            if (!_next.next?.IsValid){
                _next.next = null;
            }

            _client.next = _next;
            _client.status = 'DE';
            _place.AddClient(_next);

            return _next;
        }

        if (!_client){
            form.place = _place;
            form.name = _merchantName;
            form.taxid = _merchantTaxid;
            form.email = _merchantEmail;
            form.address = _merchantAddress;
            form.postal = _merchantPostal;
            form.region = _merchantRegion;
            form.country = _merchantCountry;
            form.status = 'AC';

            if (!form.next?.IsValid){
                form.next = null;
            }

            _place.AddClient(form);

            return form;
        }

        _client.name = _merchantName;
        _client.taxid = _merchantTaxid;
        _client.email = _merchantEmail;

        return _client;
    }

    private async _Commit(): Promise<{ committed: boolean; client: Client | null }> {
        if (!this.place?.object){
            return { committed: false, client: null };
        }

        const _client = this._ResolveClient(this.Client);
        if (!_client){
            return { committed: false, client: null };
        }
        _client.invoiced = new Date();
        
        const _committed = await this.place.DoCommit(true);
        return { committed: _committed, client: _committed ? _client : null };
    }
}
